import { ChangeEvent, FC, InputHTMLAttributes, useRef } from "react";
import styles from "./index.module.scss";
import classNames from "classnames";
import Close from "@/assets/images/RoundedClose";
import { useIsTakeOut } from "@/stores/cash-register";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onInput"> {
  onValueChange?: (value: string) => void;
  label?: string;
  error?: string;
  containerClassName?: string;
  hideRemoveButton?: boolean;
  onRemoveClick?: () => void;
  large?: boolean;
}

interface InputEvent {
  time: number;
  timeout: NodeJS.Timeout | null;
}

const Input: FC<InputProps> = (props) => {
  const {
    onValueChange,
    label,
    error,
    containerClassName,
    hideRemoveButton = false,
    large = true,
    ...rest
  } = props;

  const lastInputEventRef = useRef<InputEvent | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTakeOut = useIsTakeOut();

  const createNewTimeout = (time: number, value: string, timeout: number) => {
    lastInputEventRef.current = {
      time,
      timeout: setTimeout(() => {
        if (!!onValueChange) {
          onValueChange(value);
        }
      }, timeout),
    };
  };

  /* This logic is used to make sure that barcode scanner will not trigger the input change event */
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const lastInputEvent = lastInputEventRef.current;
    const timeSinceEpoch = new Date().getTime();
    const { value } = e.target;

    const lastChar = e.target.value[e.target.value.length - 1];
    const timeoutTime = !isNaN(parseFloat(lastChar)) ? 25 : 0;

    const shouldCreateNewTimeout =
      (!!lastInputEvent && timeSinceEpoch - lastInputEvent.time > timeoutTime) ||
      lastInputEvent === null;

    if (shouldCreateNewTimeout) return createNewTimeout(timeSinceEpoch, value, timeoutTime);

    if (!!lastInputEvent?.timeout) clearTimeout(lastInputEvent.timeout);
    lastInputEventRef.current = {
      time: timeSinceEpoch,
      timeout: null,
    };
  };

  const onChangePickup = (e: ChangeEvent<HTMLInputElement>) => {
    onValueChange?.(e.target.value);
  };

  const onRemoveClick = () => {
    if (!!onValueChange) onValueChange("");
    if (!!inputRef.current) inputRef.current.focus();
    props.onRemoveClick?.();
  };

  return (
    <div className={classNames(styles.labelWrapper, containerClassName)}>
      {label && <label htmlFor={props?.name}>{label}</label>}
      <div
        className={classNames(
          styles.container,
          large && styles.largeContainer,
          error && styles.containerError
        )}>
        <input
          {...rest}
          onInput={isTakeOut ? onChange : onChangePickup}
          value={props.value ?? ""}
          ref={inputRef}
        />
        {!hideRemoveButton && <Close onClick={onRemoveClick} className={styles.close} />}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

export default Input;
